import { useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Wallet, Plus, Trash2, X } from 'lucide-react';
import type { ExpenseCategory, FinancePeriod, GymExpense, RecordExpenseRequest } from '@techsofit/api-types';
import { useAuth } from '@techsofit/app-kit';
import { Button, Card, Input, Modal, Select, Skeleton, EmptyState, StatCard, Textarea, useConfirm, cn } from '@techsofit/ui';
import { PageHeader } from '../components/PageHeader';
import { deleteExpense, getFinanceSummary, listExpenses, recordExpense } from '../lib/gymApi';
import { useAsync } from '../lib/useAsync';
import { formatDate, money, todayIso } from '../lib/format';

const PERIODS: { value: FinancePeriod; label: string }[] = [
  { value: 'WEEK', label: 'This week' },
  { value: 'MONTH', label: 'This month' },
  { value: 'YEAR', label: 'This year' },
];

const CATEGORIES: { value: ExpenseCategory; label: string }[] = [
  { value: 'SALARY', label: 'Salary' },
  { value: 'RENT', label: 'Rent' },
  { value: 'UTILITIES', label: 'Utilities' },
  { value: 'EQUIPMENT', label: 'Equipment' },
  { value: 'MARKETING', label: 'Marketing' },
  { value: 'SUPPLIES', label: 'Supplies' },
  { value: 'MAINTENANCE', label: 'Maintenance' },
  { value: 'OTHER', label: 'Other' },
];

function categoryLabel(c: ExpenseCategory) {
  return CATEGORIES.find((x) => x.value === c)?.label ?? c;
}

function trend(current: string, previous: string, upIsGood: boolean) {
  const a = Number(current);
  const b = Number(previous);
  const diff = a - b;
  if (diff === 0) return { value: 'same', direction: 'flat' as const };
  const pct = b === 0 ? '' : `${Math.abs(Math.round((diff / b) * 100))}%`;
  return {
    value: pct || money(Math.abs(diff)),
    direction: (diff > 0 ? 'up' : 'down') as 'up' | 'down',
    good: upIsGood,
  };
}

type CategoryFilter = ExpenseCategory | 'ALL';

export function FinancePage() {
  const { user } = useAuth();
  const isOwner = user?.role === 'GYM_OWNER';
  const confirm = useConfirm();
  const [period, setPeriod] = useState<FinancePeriod>('WEEK');
  const [adding, setAdding] = useState(false);
  const [filter, setFilter] = useState<CategoryFilter>('ALL');

  const summary = useAsync(() => getFinanceSummary(period), [period]);
  const range = summary.data?.current;
  const expenses = useAsync(
    () => (range ? listExpenses(range.from, range.to) : Promise.resolve([])),
    [range?.from, range?.to],
  );

  const allRows = expenses.data ?? [];
  const byCategory = useMemo(() => {
    const map = new Map<ExpenseCategory, { total: number; count: number }>();
    for (const e of allRows) {
      const cur = map.get(e.category) ?? { total: 0, count: 0 };
      cur.total += Number(e.amount);
      cur.count += 1;
      map.set(e.category, cur);
    }
    return [...map.entries()]
      .map(([category, v]) => ({ category, ...v }))
      .sort((a, b) => b.total - a.total);
  }, [allRows]);

  const filtered = useMemo(
    () => (filter === 'ALL' ? allRows : allRows.filter((e) => e.category === filter)),
    [allRows, filter],
  );

  if (!isOwner) return <Navigate to="/dashboard" replace />;

  const cur = summary.data?.current;
  const prev = summary.data?.previous;

  async function remove(id: string, label: string) {
    if (!(await confirm({ title: 'Delete this expense?', description: label, confirmLabel: 'Delete', tone: 'danger' }))) {
      return;
    }
    await deleteExpense(id);
    expenses.reload();
    summary.reload();
  }

  function openCategory(c: CategoryFilter) {
    setFilter(c);
    document.getElementById('expense-list')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  return (
    <section className="mt-6 animate-fade-up">
      <PageHeader
        eyebrow="Money"
        title="Finance"
        description="Revenue from payments, expenses you record, and the profit left over. Tap a category to see every salary, equipment purchase, and date."
        actions={
          <Button onClick={() => setAdding(true)}>
            <Plus className="size-4" aria-hidden />
            Record expense
          </Button>
        }
      />

      <div className="mb-5 inline-flex gap-1 rounded-pill bg-surface p-1">
        {PERIODS.map((p) => (
          <button
            key={p.value}
            type="button"
            onClick={() => {
              setPeriod(p.value);
              setFilter('ALL');
            }}
            className={cn(
              'rounded-pill px-4 py-1.5 text-sm font-medium transition-colors',
              period === p.value ? 'bg-card text-ink shadow-card' : 'text-ink-soft hover:text-ink',
            )}
          >
            {p.label}
          </button>
        ))}
      </div>

      {summary.loading && !cur ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-36 rounded-card" />
          ))}
        </div>
      ) : cur && prev ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            label="Revenue"
            value={money(cur.revenue.total)}
            sub={`Membership ${money(cur.revenue.membership)} · PT ${money(cur.revenue.personalTraining)}`}
            icon={<Wallet className="size-4" aria-hidden />}
            trend={trend(cur.revenue.total, prev.revenue.total, true)}
          />
          <StatCard
            label="Expenses"
            value={money(cur.expenses.total)}
            sub={`Payroll ${money(cur.expenses.payroll)} · Other ${money(cur.expenses.other)}`}
            trend={trend(cur.expenses.total, prev.expenses.total, false)}
            onClick={() => openCategory('ALL')}
            actionLabel="View expenses"
          />
          <StatCard
            label="Payroll"
            value={money(cur.expenses.payroll)}
            sub="Salaries paid this period"
            onClick={() => openCategory('SALARY')}
            actionLabel="View salaries"
          />
          <StatCard
            label="Profit"
            value={money(cur.profit)}
            sub={`${formatDate(cur.from)} – ${formatDate(cur.to)}`}
            emphasis
            trend={trend(cur.profit, prev.profit, true)}
          />
        </div>
      ) : summary.error ? (
        <p className="text-sm text-bad">{summary.error}</p>
      ) : null}

      {byCategory.length > 0 && (
        <Card className="mt-4 p-5">
          <h2 className="font-display text-lg font-semibold text-ink">By category</h2>
          <p className="mt-0.5 text-sm text-muted">Tap a row to see the individual entries with dates and notes.</p>
          <ul className="mt-3 divide-y divide-line">
            {byCategory.map((row) => (
              <li key={row.category}>
                <button
                  type="button"
                  onClick={() => openCategory(row.category)}
                  className={cn(
                    'flex w-full items-center gap-3 py-3 text-left transition-colors hover:bg-surface',
                    filter === row.category && 'bg-surface',
                  )}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-ink">{categoryLabel(row.category)}</p>
                    <p className="text-xs text-muted">
                      {row.count} {row.count === 1 ? 'entry' : 'entries'}
                    </p>
                  </div>
                  <p className="tabular text-sm font-semibold text-ink">{money(row.total)}</p>
                </button>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Card id="expense-list" className="mt-6 overflow-hidden p-0">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-5 py-4">
          <div>
            <h2 className="font-display text-lg font-semibold text-ink">
              {filter === 'ALL' ? 'Expenses' : categoryLabel(filter)}
            </h2>
            <p className="text-sm text-muted">
              {cur ? `${formatDate(cur.from)} – ${formatDate(cur.to)}` : 'This period'}
              {filter !== 'ALL' ? ` · ${filtered.length} shown` : ''}
            </p>
          </div>
          {filter !== 'ALL' && (
            <Button size="sm" variant="ghost" onClick={() => setFilter('ALL')}>
              <X className="size-4" aria-hidden /> Clear filter
            </Button>
          )}
        </div>
        {expenses.loading && !expenses.data ? (
          <div className="space-y-2 p-5">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState
            title={filter === 'ALL' ? 'No expenses this period' : `No ${categoryLabel(filter).toLowerCase()} this period`}
            description={
              filter === 'ALL'
                ? 'Record rent, salaries, and supplies so profit is real — not just revenue.'
                : 'Try another category, or clear the filter to see everything.'
            }
          />
        ) : (
          <ul className="divide-y divide-line">
            {filtered.map((e) => (
              <ExpenseItem key={e.id} expense={e} onRemove={remove} />
            ))}
          </ul>
        )}
      </Card>

      <ExpenseModal
        open={adding}
        onClose={() => setAdding(false)}
        onSaved={() => {
          setAdding(false);
          expenses.reload();
          summary.reload();
        }}
      />
    </section>
  );
}

function ExpenseItem({
  expense: e,
  onRemove,
}: {
  expense: GymExpense;
  onRemove: (id: string, label: string) => void;
}) {
  return (
    <li className="flex items-center gap-3 px-5 py-3">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-ink">
          {categoryLabel(e.category)}
          {e.staffName ? ` · ${e.staffName}` : ''}
        </p>
        <p className="text-xs text-muted">
          {formatDate(e.incurredOn)}
          {e.note ? ` · ${e.note}` : ''}
        </p>
      </div>
      <p className="tabular text-sm font-semibold text-ink">{money(e.amount)}</p>
      <button
        type="button"
        className="rounded-control p-2 text-ink-soft hover:bg-surface hover:text-bad"
        aria-label="Delete expense"
        onClick={() =>
          onRemove(e.id, `${categoryLabel(e.category)} ${money(e.amount)} on ${formatDate(e.incurredOn)}`)
        }
      >
        <Trash2 className="size-4" aria-hidden />
      </button>
    </li>
  );
}

function ExpenseModal({
  open,
  onClose,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [incurredOn, setIncurredOn] = useState(todayIso);
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState<ExpenseCategory>('RENT');
  const [staffName, setStaffName] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isSalary = category === 'SALARY';

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const n = Number(amount);
      if (!Number.isFinite(n) || n <= 0) {
        setError('Enter an amount greater than zero.');
        return;
      }
      if (isSalary && !staffName.trim()) {
        setError('Say who this salary is for.');
        return;
      }
      const req: RecordExpenseRequest = {
        incurredOn,
        amount: n,
        category,
        staffName: staffName.trim() || undefined,
        note: note.trim() || undefined,
      };
      await recordExpense(req);
      setAmount('');
      setStaffName('');
      setNote('');
      setCategory('RENT');
      setIncurredOn(todayIso());
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the expense.');
    } finally {
      setSaving(false);
    }
  }

  const canSave = useMemo(() => amount.trim().length > 0, [amount]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Record expense"
      description="Salaries are expenses too — they reduce profit. Pick Salary and name the person so payroll stays obvious."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={save} loading={saving} disabled={!canSave}>
            Save expense
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {error && <p className="text-sm text-bad">{error}</p>}
        <Input label="Date" type="date" value={incurredOn} onChange={(e) => setIncurredOn(e.target.value)} />
        <Input
          label="Amount"
          type="number"
          inputMode="decimal"
          step="0.01"
          min="0.01"
          leading="$"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />
        <Select label="Category" value={category} onChange={(e) => setCategory(e.target.value as ExpenseCategory)}>
          {CATEGORIES.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </Select>
        {isSalary && (
          <Input
            label="Paid to"
            required
            value={staffName}
            onChange={(e) => setStaffName(e.target.value)}
            placeholder="Staff member name"
          />
        )}
        <Textarea label="Note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional" />
      </div>
    </Modal>
  );
}
